import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { deleteEntry, errorMessage, fetchDetails, formatDate, formatDateTime, PackingDetailsData } from './packingApi';
import { btn, dialog, overlay } from './packingStyles';

interface Props {
  pivotId: number;
  canDelete: boolean;
  onClose: () => void;
  onChanged: () => void;
}

/** Value chain (plan → line → rewinding → packed / defects → to pack) and the ledger of one row. */
const PackingDetailsModal: React.FC<Props> = ({ pivotId, canDelete, onClose, onChanged }) => {
  const { t } = useLanguage();
  const [data, setData] = useState<PackingDetailsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await fetchDetails(pivotId));
    } catch (e: any) {
      setError(errorMessage(e, t('packing.errorLoad')));
    }
  }, [pivotId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (id: number) => {
    if (!window.confirm(t('packing.details.confirmDelete'))) return;
    try {
      await deleteEntry(id);
      await load();
      onChanged();
    } catch (e: any) {
      setError(errorMessage(e, t('packing.errorSave')));
    }
  };

  const pcs = (n: number) => t('packing.pcs', { n });
  const c = data?.chain;
  const steps: [string, string, string?][] = c
    ? [
        [t('packing.details.planned'), pcs(c.planned)],
        ...(c.big_spool_line
          ? ([
              [t('packing.details.reported'), t('packing.details.reportedBig', { kg: c.line_reported_kg ?? 0, spools: c.spools_sent ?? 0 })],
              [t('packing.details.waiting'), t('packing.details.waitingValue', { spools: c.spools_waiting ?? 0, kg: c.waiting_kg ?? 0 })],
              [t('packing.details.rewound'), pcs(c.arrived)],
            ] as [string, string][])
          : ([[t('packing.details.reported'), pcs(c.arrived)]] as [string, string][])),
        [t('packing.details.notArrived'), pcs(c.not_arrived)],
        ...(c.surplus > 0
          ? ([
              [t('packing.details.surplus'), pcs(c.surplus)],
              [t('packing.details.acceptedSurplus'), pcs(c.accepted_surplus)],
            ] as [string, string][])
          : []),
        [t('packing.details.packed'), pcs(c.packed)],
        [t('packing.details.defects'), pcs(c.defects), c.defects > 0 ? '#b91c1c' : undefined],
        [t('packing.details.toPack'), pcs(c.to_pack), '#4338ca'],
      ]
    : [];

  return (
    <div style={overlay} onClick={onClose}>
      <div style={dialog(620)} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 19, color: '#0f172a' }}>
            {t('packing.details.title', { name: data?.row.nazwa || data?.row.indeks || '' })}
          </h2>
          <button type="button" style={btn('ghost', true)} onClick={onClose} aria-label={t('packing.modal.cancel')}>
            ✕
          </button>
        </div>
        {error && <div style={{ color: '#b91c1c', margin: '12px 0' }}>{error}</div>}
        {data && (
          <>
            <div style={{ fontSize: 13, color: '#64748b', margin: '4px 0 14px' }}>
              {[data.row.indeks, data.row.linia, data.row.ean].filter(Boolean).join(' · ')}
              {data.row.buffer_since && <> · {t('packing.details.inBufferSince', { date: formatDate(data.row.buffer_since) })}</>}
            </div>
            {data.row.priority_note && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontWeight: 600 }}>
                {t('packing.details.priorityNote', { note: data.row.priority_note })}
              </div>
            )}
            <h3 style={{ fontSize: 13, textTransform: 'uppercase', color: '#475569', margin: '0 0 6px' }}>{t('packing.details.product')}</h3>
            <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '6px 16px', margin: '0 0 18px', fontSize: 14 }}>
              {(
                [
                  ['week', data.row.tydzien], ['line', data.row.linia], ['location', data.row.location], ['material', data.row.material],
                  ['diameter', data.row.srednica], ['weight', data.row.waga], ['spool', data.row.szpula],
                  ['brand', data.row.marka], ['index', data.row.indeks], ['ean', data.row.ean],
                ] as [string, string | null][]
              ).map(([key, value]) => (
                <div key={key}>
                  <dt style={{ fontSize: 11, textTransform: 'uppercase', color: '#64748b', fontWeight: 700 }}>{t(`packing.col.${key}`)}</dt>
                  <dd style={{ margin: 0, color: '#1e293b', overflowWrap: 'anywhere' }}>{value || '—'}</dd>
                </div>
              ))}
            </dl>
            <h3 style={{ fontSize: 13, textTransform: 'uppercase', color: '#475569', margin: '0 0 6px' }}>{t('packing.details.chain')}</h3>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 8 }}>
              {steps.map(([label, value, color], i) => (
                <div
                  key={label}
                  style={{
                    display: 'flex', justifyContent: 'space-between', gap: 12, padding: '9px 12px',
                    borderTop: i ? '1px solid #f1f5f9' : 'none', fontSize: 15,
                    fontWeight: i === steps.length - 1 ? 800 : 500, color: color ?? '#1e293b',
                  }}
                >
                  <span>{label}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                </div>
              ))}
            </div>
            <p style={{ margin: '0 0 18px', fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>
              {c?.big_spool_line ? t('packing.details.notArrivedHintBig') : t('packing.details.notArrivedHint')}
            </p>
            <h3 style={{ fontSize: 13, textTransform: 'uppercase', color: '#475569', margin: '0 0 6px' }}>{t('packing.details.entries')}</h3>
            {data.entries.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 14 }}>{t('packing.details.noEntries')}</div>
            ) : (
              data.entries.map((e) => (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid #f1f5f9', fontSize: 14, flexWrap: 'wrap' }}>
                  <span style={{ minWidth: 120, color: '#64748b' }}>{formatDateTime(e.created_at)}</span>
                  <span style={{ fontWeight: 700, color: e.kind === 'defect' ? '#b91c1c' : '#166534' }}>
                    {t(`packing.kind.${e.kind}`)} {pcs(e.quantity)}
                  </span>
                  <span style={{ color: '#475569', flex: 1 }}>{[e.source === 'wfmag' ? t('packing.details.fromWfmagLabel') : e.user, e.note].filter(Boolean).join(' · ')}</span>
                  {canDelete && (
                    <button type="button" style={btn('ghost', true)} onClick={() => void remove(e.id)}>
                      {t('packing.details.delete')}
                    </button>
                  )}
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PackingDetailsModal;
